import { useEffect, useRef } from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { UserAssessmentCycleStatus } from "graphql/types/Cycle";

export const CYCLE_STATUS_POLL_INTERVAL_MS = 10_000;

export const GetCycleStatus = gql`
  query GetCycleStatus {
    me {
      id
      currentCycle {
        userAssessmentCycle {
          status
        }
      }
    }
  }
`;

interface CycleStatusData {
  me: {
    id: string;
    currentCycle: {
      userAssessmentCycle: {
        status: UserAssessmentCycleStatus;
      };
    } | null;
  };
}

const SCORING_STATES: ReadonlySet<UserAssessmentCycleStatus> = new Set([
  UserAssessmentCycleStatus.SCORING_IN_PROGRESS,
  UserAssessmentCycleStatus.READY_TO_SCORE
]);

/**
 * Polls the current cycle status every 10 seconds while the cycle is in a
 * scoring state (SCORING_IN_PROGRESS or READY_TO_SCORE). Stops polling and
 * invokes `onFinalized` when the status transitions to `score_finalized`.
 */
export default function useCycleStatusPolling(
  currentStatus: UserAssessmentCycleStatus | undefined,
  onFinalized: () => void
) {
  const shouldPoll =
    currentStatus !== undefined && SCORING_STATES.has(currentStatus);

  const { data, stopPolling } = useQuery<CycleStatusData>(GetCycleStatus, {
    pollInterval: shouldPoll ? CYCLE_STATUS_POLL_INTERVAL_MS : 0,
    skip: !shouldPoll,
    fetchPolicy: "network-only"
  });

  const onFinalizedRef = useRef(onFinalized);
  onFinalizedRef.current = onFinalized;

  const polledStatus = data?.me?.currentCycle?.userAssessmentCycle?.status;

  useEffect(() => {
    if (polledStatus === UserAssessmentCycleStatus.SCORE_FINALIZED) {
      stopPolling();
      onFinalizedRef.current();
    }
  }, [polledStatus, stopPolling]);
}
